import type { FetchLike } from "@brains/utils/fetch-like";
import type { Logger } from "@brains/utils/logger";
import { z } from "@brains/utils/zod";
import type {
  PublishProvider,
  PublishResult,
  PublishImageData,
  PublishMediaData,
} from "@brains/contracts";
import type { LinkedinConfig } from "../config";

/**
 * External HTTP dependency for the LinkedIn client.
 * Injected via the constructor so tests can pass a stub function instead of
 * mocking globalThis.fetch.
 */
export interface LinkedInClientDeps {
  fetch?: FetchLike;
}

const ERROR_BODY_MAX_LENGTH = 200;
const LINKEDIN_MEDIA_UPLOAD_REQUEST_KEY =
  "com.linkedin.digitalmedia.uploading.MediaUploadHttpRequest";

/**
 * Truncate raw LinkedIn API error bodies before logging or surfacing them in
 * thrown errors. LinkedIn occasionally echoes scopes / auth context back in
 * error bodies, and unbounded bodies can flood logs.
 */
function summarizeApiError(text: string): string {
  if (text.length <= ERROR_BODY_MAX_LENGTH) return text;
  return `${text.slice(0, ERROR_BODY_MAX_LENGTH)}… (truncated, ${text.length} bytes)`;
}

const linkedInUserInfoSchema = z.looseObject({
  sub: z.string(),
});

const linkedInMeSchema = z.looseObject({
  id: z.string(),
});

const linkedInUploadInfoSchema = z
  .looseObject({
    value: z.looseObject({
      asset: z.string(),
      uploadMechanism: z.looseObject({
        [LINKEDIN_MEDIA_UPLOAD_REQUEST_KEY]: z.looseObject({
          uploadUrl: z.url(),
        }),
      }),
    }),
  })
  .transform((data) => ({
    uploadUrl:
      data.value.uploadMechanism[LINKEDIN_MEDIA_UPLOAD_REQUEST_KEY].uploadUrl,
    assetUrn: data.value.asset,
  }));

const linkedInDocumentUploadInfoSchema = z
  .looseObject({
    value: z.looseObject({
      uploadUrl: z.url(),
      document: z.string(),
    }),
  })
  .transform((data) => ({
    uploadUrl: data.value.uploadUrl,
    documentUrn: data.value.document,
  }));

type LinkedInUploadInfo = z.output<typeof linkedInUploadInfoSchema>;
type LinkedInDocumentUploadInfo = z.output<
  typeof linkedInDocumentUploadInfoSchema
>;

interface LinkedInShareMediaAsset {
  category: "DOCUMENT" | "IMAGE";
  urn: string;
  title?: string;
}

function createShareMediaEntry(
  asset: LinkedInShareMediaAsset,
): Record<string, unknown> {
  const entry: Record<string, unknown> = {
    status: "READY",
    media: asset.urn,
  };

  if (asset.title) {
    entry["title"] = { text: asset.title };
  }

  return entry;
}

function parseUploadInfo(value: unknown): LinkedInUploadInfo | null {
  const parsed = linkedInUploadInfoSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

function parseDocumentUploadInfo(
  value: unknown,
): LinkedInDocumentUploadInfo | null {
  const parsed = linkedInDocumentUploadInfoSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

/**
 * LinkedIn provider for posting content via the Share API v2
 *
 * Requires OAuth2 access token with `w_member_social` scope for personal posting,
 * or `w_organization_social` scope for organization posting (set `organizationId` in config).
 * Access tokens expire after 60 days, refresh tokens after 1 year.
 *
 * @see https://learn.microsoft.com/en-us/linkedin/marketing/community-management/shares/posts-api
 */
const DEFAULT_LINKEDIN_REST_API_VERSION = "202604";

export class LinkedInClient implements PublishProvider {
  private config: LinkedinConfig;
  private logger: Logger;
  public readonly name = "linkedin";
  private readonly apiBaseUrl = "https://api.linkedin.com/v2";
  private readonly restApiBaseUrl = "https://api.linkedin.com/rest";
  private readonly fetch: FetchLike;
  private cachedUserId: string | null = null;

  constructor(
    config: LinkedinConfig,
    logger: Logger,
    deps: LinkedInClientDeps = {},
  ) {
    this.config = config;
    this.logger = logger;
    this.fetch = deps.fetch ?? globalThis.fetch.bind(globalThis);
  }

  /**
   * Publish a post to LinkedIn, optionally with an image or PDF document.
   * Documents take precedence over images because LinkedIn carousels are
   * represented as document posts.
   */
  async publish(
    content: string,
    _metadata: Record<string, unknown>,
    imageData?: PublishImageData,
    documentData?: PublishMediaData[],
  ): Promise<PublishResult> {
    if (!this.config.accessToken) {
      throw new Error("LinkedIn access token not configured");
    }

    // Get author URN (organization or personal)
    const author = await this.getAuthor();

    const documentAttachment = documentData?.[0];
    if (documentData && documentData.length > 1) {
      this.logger.warn("LinkedIn document publishing supports one PDF", {
        count: documentData.length,
      });
    }

    let mediaAsset: LinkedInShareMediaAsset | null = null;
    if (documentAttachment) {
      // Document uploads must succeed: the document IS the post (PDF carousel),
      // so a silent text-only fallback would publish something the caller never
      // asked for. Native document posts use LinkedIn's versioned /rest APIs;
      // keep UGC Posts below for text/image publishing.
      const documentUrn = await this.uploadDocument(author, documentAttachment);
      return this.publishDocumentPost(
        author,
        content,
        documentUrn,
        documentAttachment.filename,
      );
    } else if (imageData) {
      const assetUrn = await this.uploadImage(author, imageData);
      if (assetUrn) {
        mediaAsset = { category: "IMAGE", urn: assetUrn };
      }
    }

    // Create the post using UGC Posts API
    const shareContent: Record<string, unknown> = {
      shareCommentary: {
        text: content,
      },
      shareMediaCategory: mediaAsset?.category ?? "NONE",
      ...(mediaAsset && {
        media: [createShareMediaEntry(mediaAsset)],
      }),
    };

    const response = await this.fetch(`${this.apiBaseUrl}/ugcPosts`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${this.config.accessToken}`,
        "Content-Type": "application/json",
        "X-Restli-Protocol-Version": "2.0.0",
      },
      body: JSON.stringify({
        author,
        lifecycleState: "PUBLISHED",
        specificContent: {
          "com.linkedin.ugc.ShareContent": shareContent,
        },
        visibility: {
          "com.linkedin.ugc.MemberNetworkVisibility": "PUBLIC",
        },
      }),
    });

    if (!response.ok) {
      const errorText = summarizeApiError(await response.text());
      this.logger.error("LinkedIn API error", {
        status: response.status,
        error: errorText,
      });
      throw new Error(`LinkedIn API error: ${response.status} - ${errorText}`);
    }

    // Extract post ID from response headers or body
    const postId = response.headers.get("X-RestLi-Id") ?? "";

    this.logger.info("LinkedIn post created", {
      postId,
      mediaCategory: mediaAsset?.category ?? "NONE",
    });

    const result: PublishResult = { id: postId };
    if (postId) {
      result.url = `https://www.linkedin.com/feed/update/${postId}`;
    }
    return result;
  }

  /**
   * Headers required by LinkedIn's versioned /rest APIs.
   */
  private getRestHeaders(): Record<string, string> {
    return {
      Authorization: `Bearer ${this.config.accessToken}`,
      "Content-Type": "application/json",
      "Linkedin-Version":
        this.config.apiVersion ?? DEFAULT_LINKEDIN_REST_API_VERSION,
      "X-Restli-Protocol-Version": "2.0.0",
    };
  }

  /**
   * Upload an image to LinkedIn and return the asset URN
   * Returns null if upload fails (allows graceful fallback to text-only)
   */
  private async uploadImage(
    author: string,
    imageData: PublishImageData,
  ): Promise<string | null> {
    try {
      // Step 1: Register the upload
      const registerResponse = await this.fetch(
        `${this.apiBaseUrl}/assets?action=registerUpload`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${this.config.accessToken}`,
            "Content-Type": "application/json",
            "X-Restli-Protocol-Version": "2.0.0",
          },
          body: JSON.stringify({
            registerUploadRequest: {
              recipes: ["urn:li:digitalmediaRecipe:feedshare-image"],
              owner: author,
              serviceRelationships: [
                {
                  relationshipType: "OWNER",
                  identifier: "urn:li:userGeneratedContent",
                },
              ],
            },
          }),
        },
      );

      if (!registerResponse.ok) {
        const errorText = summarizeApiError(await registerResponse.text());
        this.logger.warn("LinkedIn image upload registration failed", {
          status: registerResponse.status,
          error: errorText,
        });
        return null;
      }

      const uploadInfo = parseUploadInfo(await registerResponse.json());
      if (!uploadInfo) {
        this.logger.warn("LinkedIn image upload registration was malformed");
        return null;
      }

      const { uploadUrl, assetUrn } = uploadInfo;

      // Step 2: Upload the binary image data
      // Create Uint8Array view for fetch compatibility (works in Node, Bun, browser)
      const uploadResponse = await this.fetch(uploadUrl, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${this.config.accessToken}`,
          "Content-Type": imageData.mimeType,
        },
        body: new Uint8Array(imageData.data),
      });

      if (!uploadResponse.ok) {
        this.logger.warn("LinkedIn image binary upload failed", {
          status: uploadResponse.status,
        });
        return null;
      }

      this.logger.info("LinkedIn image uploaded", { assetUrn });
      return assetUrn;
    } catch (error) {
      // Deliberately unlike a document upload, which throws: a post that lost
      // its image is still the text the author wrote, and publishing it beats
      // failing the whole post. The text-only fallback has its own test.
      this.logger.warn("LinkedIn image upload error", { error });
      return null;
    }
  }

  /**
   * Upload a PDF document to LinkedIn and return the native document URN.
   * Throws on any failure — the document is the post, so silent fallback
   * would mislead the caller about what was published.
   */
  private async uploadDocument(
    author: string,
    documentData: PublishMediaData,
  ): Promise<string> {
    const registerResponse = await this.fetch(
      `${this.restApiBaseUrl}/documents?action=initializeUpload`,
      {
        method: "POST",
        headers: this.getRestHeaders(),
        body: JSON.stringify({
          initializeUploadRequest: {
            owner: author,
          },
        }),
      },
    );

    if (!registerResponse.ok) {
      const errorText = summarizeApiError(await registerResponse.text());
      throw new Error(
        `LinkedIn document upload initialization failed: ${registerResponse.status} - ${errorText}`,
      );
    }

    const uploadInfo = parseDocumentUploadInfo(await registerResponse.json());
    if (!uploadInfo) {
      throw new Error("LinkedIn document upload initialization was malformed");
    }

    const uploadResponse = await this.fetch(uploadInfo.uploadUrl, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${this.config.accessToken}`,
        "Content-Type": documentData.mimeType,
      },
      body: new Uint8Array(documentData.data),
    });

    if (!uploadResponse.ok) {
      throw new Error(
        `LinkedIn document binary upload failed: ${uploadResponse.status}`,
      );
    }

    this.logger.info("LinkedIn document uploaded", {
      documentUrn: uploadInfo.documentUrn,
      filename: documentData.filename,
    });
    return uploadInfo.documentUrn;
  }

  /**
   * Publish a native LinkedIn document post via the versioned /rest Posts API.
   */
  private async publishDocumentPost(
    author: string,
    content: string,
    documentUrn: string,
    title: string,
  ): Promise<PublishResult> {
    const response = await this.fetch(`${this.restApiBaseUrl}/posts`, {
      method: "POST",
      headers: this.getRestHeaders(),
      body: JSON.stringify({
        author,
        commentary: content,
        visibility: "PUBLIC",
        distribution: {
          feedDistribution: "MAIN_FEED",
          targetEntities: [],
          thirdPartyDistributionChannels: [],
        },
        content: {
          media: {
            id: documentUrn,
            title,
          },
        },
        lifecycleState: "PUBLISHED",
        isReshareDisabledByAuthor: false,
      }),
    });

    if (!response.ok) {
      const errorText = summarizeApiError(await response.text());
      this.logger.error("LinkedIn document post API error", {
        status: response.status,
        error: errorText,
      });
      throw new Error(
        `LinkedIn document post API error: ${response.status} - ${errorText}`,
      );
    }

    const postId = response.headers.get("X-RestLi-Id") ?? "";
    this.logger.info("LinkedIn document post created", {
      postId,
      documentUrn,
    });

    const result: PublishResult = { id: postId };
    if (postId) {
      result.url = `https://www.linkedin.com/feed/update/${postId}`;
    }
    return result;
  }

  /**
   * Validate that credentials are configured and working
   */
  async validateCredentials(): Promise<boolean> {
    if (!this.config.accessToken) {
      return false;
    }

    try {
      if (this.config.organizationId) {
        const response = await this.fetch(
          `${this.apiBaseUrl}/organizations/${this.config.organizationId}`,
          {
            headers: {
              Authorization: `Bearer ${this.config.accessToken}`,
            },
          },
        );
        return response.ok;
      }

      await this.getUserId();
      return true;
    } catch {
      // Credentials we cannot check are credentials we cannot use, so
      // unreachable and invalid are the same answer to this question.
      return false;
    }
  }

  /**
   * Get the author URN for posting.
   * Returns organization URN if organizationId is configured, otherwise personal URN.
   */
  private async getAuthor(): Promise<string> {
    if (this.config.organizationId) {
      return `urn:li:organization:${this.config.organizationId}`;
    }
    return this.getUserId();
  }

  /**
   * Get the current user's LinkedIn ID (URN)
   * Tries /v2/userinfo first (requires openid scope), falls back to /v2/me
   */
  private async getUserId(): Promise<string> {
    if (this.cachedUserId) {
      return this.cachedUserId;
    }

    if (!this.config.accessToken) {
      throw new Error("LinkedIn access token not configured");
    }

    // Try OpenID Connect userinfo endpoint first (requires openid scope)
    try {
      const userinfoResponse = await this.fetch(
        "https://api.linkedin.com/v2/userinfo",
        {
          headers: {
            Authorization: `Bearer ${this.config.accessToken}`,
          },
        },
      );

      if (userinfoResponse.ok) {
        const parsedUserInfo = linkedInUserInfoSchema.safeParse(
          await userinfoResponse.json(),
        );
        if (parsedUserInfo.success) {
          this.cachedUserId = `urn:li:person:${parsedUserInfo.data.sub}`;
          return this.cachedUserId;
        }
      }
    } catch {
      // Fall through to /v2/me
    }

    // Fall back to /v2/me endpoint (works with w_member_social)
    const meResponse = await this.fetch("https://api.linkedin.com/v2/me", {
      headers: {
        Authorization: `Bearer ${this.config.accessToken}`,
      },
    });

    if (!meResponse.ok) {
      const errorText = summarizeApiError(await meResponse.text());
      throw new Error(
        `Failed to get LinkedIn user ID: ${meResponse.status} - ${errorText}`,
      );
    }

    const meData = linkedInMeSchema.parse(await meResponse.json());
    this.cachedUserId = `urn:li:person:${meData.id}`;
    return this.cachedUserId;
  }
}

/**
 * Create a LinkedIn provider instance
 */
export function createLinkedInProvider(
  config: LinkedinConfig,
  logger: Logger,
  deps: LinkedInClientDeps = {},
): PublishProvider {
  return new LinkedInClient(config, logger, deps);
}
